import React from 'react'
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material'
import {
  Link as GatsbyLink,
  GatsbyLinkProps as GatsbyLinkPropsType,
} from 'gatsby'
import useTidyLink from '../../hooks/useTidyLink'
import ExternalLink from './ExternalLink'

type EmptyGatsbyLinkState = Record<string, never>

interface LinkWithGatsbyProps<GLState = EmptyGatsbyLinkState>
  extends Omit<MuiLinkProps, 'href'> {
  to: string
  GatsbyLinkProps?: Omit<GatsbyLinkPropsType<GLState>, 'to'>
}

export type LinkProps<GLState = EmptyGatsbyLinkState> =
  | MuiLinkProps
  | LinkWithGatsbyProps<GLState>

/**
 * Link is a wrapper component that fuses Material UI's Link component with
 * Gatsby's Link component conditionally while also allowing linking to external
 * sites.
 *
 * If the prop `to` is provided, the Gatsby <Link /> component will be used and
 * it will use it's fanciness around preloading and page transitions. If `href`
 * is provided, it is treated just like a regular `<a />` tag.
 */
const Link = React.forwardRef((props: LinkProps, ref) => {
  const tidyTheLink = useTidyLink()

  if ('to' in props) {
    const { to, GatsbyLinkProps = {}, ...muiLinkProps } = props
    const tidyTo = tidyTheLink(decodeURIComponent(to))

    return (
      <MuiLink
        underline="hover"
        // @ts-ignore
        ref={ref}
        component={GatsbyLink}
        to={tidyTo}
        {...muiLinkProps}
        {...GatsbyLinkProps}
      />
    )
  }

  return <ExternalLink ref={ref} {...props} />
})

export default Link
